// @ts-nocheck
import { Fragment, useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Drawer,
  Chip
} from '@mui/material';
import {
  Check,
  CircleNotch,
  File,
  PaperPlaneRight,
  Plus,
  Sparkle,
  User,
  X
} from '@phosphor-icons/react';
import { XGButton } from 'xg-cl';
import globalConfig from '../../config/globalConfig';
import { ErrorToast } from '../../utils/alerts';
import Editor from '../Editor/Editor';
import useProseSpellCheckAttributes from '../../hooks/useProseSpellCheckAttributes';
import * as PropTypes from 'prop-types';
import './AIChat.css';
import {
  isSupportedImageFile,
  SUPPORTED_IMAGE_EXTENSIONS
} from '../../utils/supportedImages';

const MUTATION_TOOL_NAME_PATTERN =
  /^(?:update|create|delete|set|clear|propose|accept|reject|verify)(?:_|$)/;
const RESULT_GATED_MUTATION_TOOLS = new Set(['apply_passenger_field_rule']);

// Progress labels for the tools the agent runs while it works on a reply.
const TOOL_STEP_LABEL_KEYS = {
  get_trip_passenger_fields: 'passengers:aiChatStepTripFields',
  get_booking: 'passengers:aiChatStepBooking',
  get_all_bookings: 'passengers:aiChatStepBookings',
  search_bookings: 'passengers:aiChatStepBookings',
  get_trip: 'passengers:aiChatStepTrip',
  get_all_trips: 'passengers:aiChatStepTrips',
  search_trips: 'passengers:aiChatStepTrips',
  get_passenger: 'passengers:aiChatStepPassengers',
  get_trip_passengers: 'passengers:aiChatStepPassengers',
  search_passengers: 'passengers:aiChatStepPassengers',
  propose_passenger_field: 'passengers:aiChatStepPropose',
  propose_passenger_fields_bulk: 'passengers:aiChatStepPropose',
  propose_passengers_bulk: 'passengers:aiChatStepPropose',
  get_skill: 'passengers:aiChatStepSkill'
};

const markToolStepsDone = (steps) =>
  steps.map((step) =>
    step.status === 'running' ? { ...step, status: 'done' } : step
  );

// The text sent back as history for one message: the reply itself plus any
// notes about actions taken outside the text (for example a card click).
const toHistoryContent = (message, additionalInstructions) => {
  const text = `${message.content.trim()}${message.role === 'user' ? additionalInstructions : ''}`;
  const notes = message.historyNotes?.length
    ? message.historyNotes.join('\n')
    : '';

  return [text, notes].filter(Boolean).join('\n\n');
};

const AIChat = ({
  isOpen,
  onClose,
  contextType = 'global',
  contextId = null,
  title = 'AI Assistant',
  titleIcon = null,
  onDataUpdate,
  embedded = false,
  showInstructions = true,
  showSubtitle = true,
  additionalInstructions = '',
  customInput = '',
  setResultData = () => {},
  children = null,
  emptyStateTitle = null,
  emptyStateSubtitle = null,
  inputPlaceholder = null,
  captureToolResult = null,
  renderMessageAddon = null,
  renderTopContent = null,
  variant = 'default',
  ...rest
}) => {
  const { t } = useTranslation(['common', 'passengers']);
  const proseSpellCheckAttributes = useProseSpellCheckAttributes();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState(null); // { name, mimeType, file, isImage, previewUrl }
  const [isDragging, setIsDragging] = useState(false);
  const sessionIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const clearAttachment = useCallback(() => {
    setAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // Notes about actions taken outside the text (for example a card click) are
  // sent with that message in later turns, so the agent knows what happened.
  const appendHistoryNote = useCallback((messageIndex, note) => {
    if (!note) return;

    setMessages((prev) =>
      prev.map((message, index) =>
        index === messageIndex
          ? {
              ...message,
              historyNotes: [...(message.historyNotes ?? []), note]
            }
          : message
      )
    );
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Nothing to follow yet. Scrolling here would push the empty state and
    // any card above the conversation (such as changes waiting for review)
    // out of view when the panel opens.
    if (messages.length === 0) return;

    scrollToBottom();
  }, [messages]);

  // Reset messages when drawer opens with new context
  useEffect(() => {
    if (isOpen) {
      setMessages([]);
      setInput('');
      clearAttachment();
      sessionIdRef.current = crypto.randomUUID();
    }
  }, [isOpen, contextType, contextId, clearAttachment]);

  const ACCEPTED_FILE_TYPES = `${SUPPORTED_IMAGE_EXTENSIONS},.xlsx,.xls,.csv,.pdf,.msg,application/vnd.ms-outlook`;
  const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv', '.pdf', '.msg'];

  const isFileAccepted = (file) => {
    if (file.type.startsWith('image/')) return isSupportedImageFile(file);
    const name = file.name.toLowerCase();
    return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const processFile = (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      ErrorToast(t('passengers:aiChatFileTooLargeError'));
      return;
    }
    const isImage = file.type.startsWith('image/');
    setAttachment({
      name: file.name,
      mimeType: file.type,
      file,
      isImage,
      previewUrl: isImage ? URL.createObjectURL(file) : null
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isFileAccepted(file)) {
      ErrorToast(t('passengers:aiChatFileTypeError'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    processFile(file);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!isFileAccepted(file)) {
      ErrorToast(t('passengers:aiChatFileTypeError'));
      return;
    }
    processFile(file);
  };

  const getLocalizedContextType = () => {
    switch (contextType) {
      case 'passenger':
        return t('common:passengerLower');
      case 'booking':
        return t('passengers:bookingId');
      case 'trip':
        return t('common:overview').toLowerCase();
      default:
        return contextType;
    }
  };

  const sendMessage = async (messageOverride = null) => {
    const currentInput =
      typeof messageOverride === 'string' && messageOverride
        ? messageOverride
        : input;

    if ((!currentInput?.trim() && !attachment) || loading) return;

    if (!sessionIdRef?.current) {
      sessionIdRef.current = crypto.randomUUID();
    }

    const userMessage =
      currentInput?.trim() ||
      (attachment
        ? t('passengers:aiChatFileUploaded', { fileName: attachment.name })
        : '');
    const currentAttachment = attachment;

    setInput('');
    clearAttachment();

    const newMessage = { role: 'user', content: userMessage };
    if (currentAttachment?.isImage) {
      newMessage.imagePreview = currentAttachment.previewUrl;
    } else if (currentAttachment) {
      newMessage.fileName = currentAttachment.name;
    }

    const conversationHistory = [...messages, newMessage];

    setMessages(conversationHistory);
    setLoading(true);

    try {
      const context = { type: contextType };
      if (contextType === 'trip' && contextId) {
        context.tripId = parseInt(contextId);
      } else if (contextType === 'booking' && contextId) {
        context.bookingId = parseInt(contextId);
      } else if (contextType === 'passenger' && contextId) {
        context.passengerId = parseInt(contextId);
      }

      const headers = {
        'accept-language': 'en',
        origin: window.location.origin
      };

      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const apiUrl =
        globalConfig.axios.apiUrl || 'https://dev.softrip.com:8000';
      const url = `${apiUrl}/ai/chat/stream`;

      const formData = new FormData();
      formData.append(
        'data',
        JSON.stringify({
          sessionId: sessionIdRef.current,
          messages: conversationHistory
            .map((m) => ({
              role: m.role,
              content: toHistoryContent(m, additionalInstructions)
            }))
            // A reply can be tool activity only; an empty turn is rejected.
            .filter((m) => m.content),
          context
        })
      );
      if (currentAttachment) {
        formData.append('file', currentAttachment.file, currentAttachment.name);
      }

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let toolSteps = [];
      let capturedToolResults = [];
      let streamHadError = false;
      let shouldRefreshData = false;
      let pendingText = '';

      // Keep the fields a message already has (such as captured tool results)
      // when streaming more text into it.
      const updateAssistantMessage = (patch) => {
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastIndex = newMessages.length - 1;
          newMessages[lastIndex] = {
            ...newMessages[lastIndex],
            role: 'assistant',
            ...patch
          };
          return newMessages;
        });
      };

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', toolSteps: [], pending: true }
      ]);

      const handleStreamLine = (line) => {
        if (!line.startsWith('data: ')) return;

        const data = line.slice(6);
        if (data === '[DONE]') return;

        try {
          const parsed = JSON.parse(data);

          if (parsed.type === 'text_delta' && parsed.text) {
            assistantMessage += parsed.text;
            updateAssistantMessage({ content: assistantMessage });
          }

          if (parsed.type === 'tool_executing') {
            // Tool activity shows as progress steps and never goes into the
            // reply text. That text is sent back as history, and the agent
            // copies what it sees there: it once wrote "Executing: …" lines
            // and "Proposed …" without calling any tool (XGE-3498).
            if (assistantMessage.trim() && !assistantMessage.endsWith('\n\n')) {
              assistantMessage += '\n\n';
            }
            toolSteps = [
              ...markToolStepsDone(toolSteps),
              { toolName: parsed.toolName, status: 'running' }
            ];
            updateAssistantMessage({ content: assistantMessage, toolSteps });
          }

          if (parsed.type === 'tool_result') {
            const runningIndex = toolSteps.findLastIndex(
              (step) =>
                step.toolName === parsed.toolName && step.status === 'running'
            );
            if (runningIndex > -1) {
              toolSteps = toolSteps.map((step, stepIndex) =>
                stepIndex === runningIndex ? { ...step, status: 'done' } : step
              );
              updateAssistantMessage({ toolSteps });
            }

            if (
              parsed.result &&
              typeof captureToolResult === 'function' &&
              captureToolResult(parsed.toolName)
            ) {
              capturedToolResults = [
                ...capturedToolResults,
                { toolName: parsed.toolName, result: parsed.result }
              ];
              updateAssistantMessage({
                content: assistantMessage,
                toolResults: capturedToolResults
              });
            }

            if (onDataUpdate) {
              const toolNameLower = String(parsed.toolName ?? '').toLowerCase();
              const explicitlyMutated =
                parsed.result?.mutated === true || parsed.mutated === true;
              const isModificationTool =
                explicitlyMutated ||
                (!RESULT_GATED_MUTATION_TOOLS.has(toolNameLower) &&
                  MUTATION_TOOL_NAME_PATTERN.test(toolNameLower));

              if (isModificationTool) {
                shouldRefreshData = true;
              }
            }
          }

          if (parsed.type === 'error') {
            streamHadError = true; //  mark error
            assistantMessage += `\n\n${t('passengers:aiChatErrorMessage', {
              message: parsed.error
            })}`;
            updateAssistantMessage({ content: assistantMessage, error: true });
          }
        } catch (e) {
          // Skip invalid JSON.
        }
      };

      // A network read can end mid-event (large tool results often do), so
      // carry any unfinished line over to the next read instead of dropping it.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        pendingText += decoder.decode(value ?? new Uint8Array(), {
          stream: !done
        });

        const lines = pendingText.split('\n');
        pendingText = done ? '' : lines.pop();
        lines.forEach(handleStreamLine);

        if (done) break;
      }

      // The reply is complete: its progress steps are no longer shown.
      updateAssistantMessage({ toolSteps: [], pending: false });

      if (shouldRefreshData && onDataUpdate) {
        await onDataUpdate();
      }

      //  Only call setResultData when:
      //    - a handler was provided (Reports page only)
      //    - stream finished without errors
      //    - there is actual content
      if (setResultData && !streamHadError && assistantMessage.trim()) {
        setResultData(assistantMessage);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: t('passengers:aiChatErrorMessage', {
            message: error.message
          }),
          error: true
        }
      ]);
      //  Do NOT call setResultData on catch — keeps result panel clear on failure
    } finally {
      setMessages((prev) =>
        prev.map((message) =>
          message.pending
            ? { ...message, toolSteps: [], pending: false }
            : message
        )
      );
      setLoading(false);
    }
  };

  const renderMessageRole = (role) => (
    <Box className='ai-chat-message-role'>
      {role === 'user' ? (
        <User size={13} weight='bold' aria-hidden='true' />
      ) : (
        <Sparkle size={13} weight='fill' aria-hidden='true' />
      )}
      <span>
        {role === 'user'
          ? t('passengers:aiChatUserLabel')
          : t('passengers:aiChatAssistantLabel')}
      </span>
    </Box>
  );

  // While a reply is in progress: the tools it has run so far, then either
  // the running tool's spinner or typing dots.
  const renderProgress = (steps = []) => {
    const hasRunningStep = steps.some((step) => step.status === 'running');

    return (
      <>
        {steps.length > 0 && (
          <Box component='ul' className='ai-chat-steps'>
            {steps.map((step, stepIndex) => (
              <Box
                component='li'
                key={`${step.toolName}-${stepIndex}`}
                className={`ai-chat-step ai-chat-step--${step.status}`}
              >
                {step.status === 'running' ? (
                  <CircleNotch
                    size={14}
                    weight='bold'
                    className='ai-chat-step__spinner'
                    aria-hidden='true'
                  />
                ) : (
                  <Check size={14} weight='bold' aria-hidden='true' />
                )}
                <span>
                  {t(
                    TOOL_STEP_LABEL_KEYS[step.toolName] ??
                      'passengers:aiChatStepWorking'
                  )}
                </span>
              </Box>
            ))}
          </Box>
        )}
        {!hasRunningStep && (
          <Box
            className='ai-chat-typing'
            role='status'
            aria-label={t('passengers:aiChatThinking')}
          >
            <span />
            <span />
            <span />
          </Box>
        )}
      </>
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    if (customInput?.trim()) {
      setInput(customInput);
      sendMessage(customInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customInput]);

  const content = (
    <Box
      className={`ai-chat-container${variant === 'panel' ? ' ai-chat-container--panel' : ''}${isDragging ? ' drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {!embedded && (
        <Box className='ai-chat-header'>
          <Box className='ai-chat-header-title'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {titleIcon}
              <Typography variant='h6' className='ai-chat-title'>
                {title}
              </Typography>
            </Box>
            <XGButton
              className='ai-chat-close-btn'
              type='tertiary'
              size='small'
              onClick={onClose}
              startIcon={<X size={21} />}
              variant='text'
              id='ai-chat-close-button'
            />
          </Box>
        </Box>
      )}

      <Box className='ai-chat-content'>
        {showInstructions && showSubtitle && (
          <Typography
            variant='body2'
            color='text.secondary'
            className='ai-chat-subtitle'
          >
            {t('passengers:aiChatSubtitle', {
              contextType: getLocalizedContextType()
            })}
          </Typography>
        )}

        <Box className='ai-chat-messages'>
          {renderTopContent && (
            <Box className='ai-chat-top'>
              {renderTopContent(messages, { isLoading: loading })}
            </Box>
          )}

          {messages.length === 0 && (
            <Box className='ai-chat-empty'>
              {titleIcon && (
                <Box
                  className='ai-chat-empty-icon'
                  sx={{ mb: 2, opacity: 0.3 }}
                >
                  {titleIcon}
                </Box>
              )}
              <Typography
                variant='body1'
                color='text.secondary'
                sx={{ fontWeight: 600 }}
              >
                {emptyStateTitle || t('passengers:aiChatEmptyTitle')}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 1, textAlign: 'center' }}
              >
                {emptyStateSubtitle ||
                  t('passengers:aiChatEmptySubtitle', {
                    contextType: getLocalizedContextType()
                  })}
              </Typography>
            </Box>
          )}

          {messages.map((message, index) => (
            <Fragment key={index}>
              {(message.role === 'user' ||
                message.pending ||
                message.error ||
                message.content.trim()) && (
                <Box
                  className={`ai-chat-message ${message.role} ${message.error ? 'error' : ''}`}
                >
                  {renderMessageRole(message.role)}
                  {message.role === 'assistant' ? (
                    <>
                      {message.content.trim() && (
                        <Box className='ai-chat-message-content'>
                          <Editor
                            content={message.content.trim()}
                            editable={false}
                            showActions={false}
                          />
                        </Box>
                      )}
                      {message.pending && renderProgress(message.toolSteps)}
                    </>
                  ) : (
                    <Box className='ai-chat-message-content'>
                      {message.imagePreview && (
                        <Box sx={{ mb: 1 }}>
                          <img
                            src={message.imagePreview}
                            alt={t('passengers:aiChatUploadedImageAlt')}
                            style={{
                              maxWidth: '300px',
                              maxHeight: '200px',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0'
                            }}
                          />
                        </Box>
                      )}
                      {message.fileName && (
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            icon={<File size={16} />}
                            label={message.fileName}
                            size='small'
                            variant='outlined'
                          />
                        </Box>
                      )}
                      <Box sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {renderMessageAddon?.(message, {
                index,
                messages,
                isLoading: loading,
                appendHistoryNote,
                sendMessage
              })}
            </Fragment>
          ))}

          {/* Waiting for the reply to start: the agent's bubble with dots. */}
          {loading && messages[messages.length - 1]?.role === 'user' && (
            <Box className='ai-chat-message assistant'>
              {renderMessageRole('assistant')}
              {renderProgress([])}
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>
      </Box>
      {children && !loading && <Box sx={{ padding: '16px' }}> {children} </Box>}

      <Box className='ai-chat-footer'>
        <Box className='ai-chat-input'>
          <input
            ref={fileInputRef}
            type='file'
            accept={ACCEPTED_FILE_TYPES}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <XGButton
            id='ai-chat-add-file-button'
            aria-label={t('passengers:aiChatAttachFile')}
            type='secondary'
            variant='outlined'
            size='medium'
            icon={<Plus size={20} />}
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            sx={{ mr: 1, minWidth: 'auto' }}
          />
          <Box
            sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 1 }}
          >
            {attachment?.isImage && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  maxWidth: '200px'
                }}
              >
                <img
                  src={attachment.previewUrl}
                  alt={t('passengers:aiChatPreviewImageAlt')}
                  style={{
                    maxWidth: '200px',
                    maxHeight: '150px',
                    borderRadius: '8px',
                    border: '2px solid #1976d2'
                  }}
                />
                <Button
                  size='small'
                  onClick={clearAttachment}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    minWidth: 'auto',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' }
                  }}
                >
                  x
                </Button>
              </Box>
            )}
            {attachment && !attachment.isImage && (
              <Box>
                <Chip
                  icon={<File size={16} />}
                  label={attachment.name}
                  size='small'
                  variant='outlined'
                  onDelete={clearAttachment}
                />
              </Box>
            )}
            <TextField
              fullWidth
              multiline
              minRows={variant === 'panel' ? 2 : 1}
              maxRows={variant === 'panel' ? 6 : 4}
              placeholder={
                inputPlaceholder || t('passengers:aiChatInputPlaceholder')
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyUp={handleKeyPress}
              disabled={loading}
              variant='outlined'
              size='small'
              inputProps={proseSpellCheckAttributes}
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.9rem' }
              }}
            />
          </Box>
          <XGButton
            id='ai-chat-send-button'
            aria-label={t('passengers:aiChatSend')}
            type='primary'
            variant='contained'
            size='medium'
            icon={<PaperPlaneRight size={20} />}
            onClick={sendMessage}
            disabled={loading || (!input.trim() && !attachment)}
            sx={{ ml: 0.5, minWidth: 'auto' }}
          />
        </Box>
      </Box>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      anchor='right'
      ModalProps={{
        BackdropProps: {
          invisible: true
        },
        disableEnforceFocus: true,
        disableAutoFocus: true,
        disableRestoreFocus: true
      }}
      {...rest}
    >
      {content}
    </Drawer>
  );
};

AIChat.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contextType: PropTypes.oneOf(['global', 'trip', 'booking', 'passenger']),
  contextId: PropTypes.number,
  title: PropTypes.string,
  onDataUpdate: PropTypes.func,
  embedded: PropTypes.bool,
  showInstructions: PropTypes.bool,
  showSubtitle: PropTypes.bool,
  customInput: PropTypes.string,
  setResultData: PropTypes.func,
  titleIcon: PropTypes.node,
  emptyStateTitle: PropTypes.string,
  emptyStateSubtitle: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  // (toolName) => boolean: keep that tool's streamed result on the message.
  captureToolResult: PropTypes.func,
  // (message, { index, messages, isLoading, appendHistoryNote, sendMessage })
  // => node rendered under the message.
  renderMessageAddon: PropTypes.func,
  // (messages, { isLoading }) => node rendered above the conversation.
  renderTopContent: PropTypes.func,
  // 'panel': the chat inside a floating panel (tighter composer, no border).
  variant: PropTypes.oneOf(['default', 'panel'])
};

export default AIChat;
